import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { WEEKDAYS, localISO } from './CalendarGrid';

const HOUR_PX = 56;
const START_HOUR = 8;
const SNAP = 15;
const toMin = (hhmm) => { const [h, m] = (hhmm || '00:00').split(':').map(Number); return h * 60 + (m || 0); };
const fmt = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

/* Reparte eventos solapados en columnas (como Google Calendar). */
const layoutDay = (items) => {
  const evs = items.map((it) => {
    const s = toMin(it.time);
    const e = it.end_time ? Math.max(toMin(it.end_time), s + 15) : s + 60;
    return { it, s, e };
  }).sort((a, b) => a.s - b.s || b.e - a.e);
  const out = [];
  let cluster = [], clusterEnd = -1;
  const flush = () => {
    if (!cluster.length) return;
    const cols = [];
    for (const ev of cluster) {
      let c = cols.findIndex((end) => end <= ev.s);
      if (c === -1) { c = cols.length; cols.push(ev.e); } else cols[c] = ev.e;
      ev.col = c;
    }
    for (const ev of cluster) out.push({ ...ev, cols: cols.length });
    cluster = []; clusterEnd = -1;
  };
  for (const ev of evs) {
    if (cluster.length && ev.s >= clusterEnd) flush();
    cluster.push(ev); clusterEnd = Math.max(clusterEnd, ev.e);
  }
  flush();
  return out;
};

const canDrag = (it) => it.can_edit && it.source_kind === 'calendar_event';

export function WeekTimeGrid({ days, items, cfgOf, onItemClick, onSlotClick, onItemMove }) {
  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
  const [drag, setDrag] = useState(null); // { id, mode, s, e, dayIdx, origS, origE, origDay, startX, startY, moved }
  useEffect(() => { const id = setInterval(() => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); }, 60000); return () => clearInterval(id); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = START_HOUR * HOUR_PX - 8; }, []);

  const todayISO = localISO(new Date());
  const dayIsos = days.map(localISO);
  const byDay = useMemo(() => {
    const map = {};
    for (const iso of dayIsos) map[iso] = { allDay: [], timed: [] };
    for (const it of items || []) {
      const bucket = map[it.date];
      if (!bucket) continue;
      if (it.all_day || !it.time || it.kind === 'task') bucket.allDay.push(it); else bucket.timed.push(it);
    }
    return map;
  }, [dayIsos.join(','), items]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSlot = (e, iso) => {
    if (!onSlotClick || drag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const min = Math.floor(((e.clientY - rect.top) / HOUR_PX) * 60 / 30) * 30;
    onSlotClick(iso, fmt(clamp(min, 0, 23 * 60 + 30)));
  };

  // ---- arrastrar / redimensionar ----
  const startDrag = (ev, it, s, e, dayIdx, mode) => {
    if (!canDrag(it) || !onItemMove) return;
    ev.stopPropagation(); ev.preventDefault();
    setDrag({ id: it.id, it, mode, s, e, dayIdx, origS: s, origE: e, origDay: dayIdx, startX: ev.clientX, startY: ev.clientY, moved: false });
  };
  useEffect(() => {
    if (!drag) return;
    const onMove = (ev) => {
      const dMin = Math.round(((ev.clientY - drag.startY) / HOUR_PX) * 60 / SNAP) * SNAP;
      let dayIdx = drag.origDay;
      if (drag.mode === 'move' && gridRef.current && days.length > 1) {
        const rect = gridRef.current.getBoundingClientRect();
        const colW = (rect.width - 56) / days.length;
        dayIdx = clamp(Math.floor((ev.clientX - rect.left - 56) / colW), 0, days.length - 1);
      }
      const dur = drag.origE - drag.origS;
      let s = drag.origS, e = drag.origE;
      if (drag.mode === 'move') { s = clamp(drag.origS + dMin, 0, 24 * 60 - dur); e = s + dur; }
      else { e = clamp(drag.origE + dMin, drag.origS + SNAP, 24 * 60); }
      const moved = drag.moved || Math.abs(ev.clientY - drag.startY) > 4 || dayIdx !== drag.origDay;
      setDrag((d) => d && { ...d, s, e, dayIdx, moved });
    };
    const onUp = () => {
      const d = drag;
      setDrag(null);
      if (!d) return;
      if (!d.moved) { onItemClick?.(d.it); return; }
      if (d.s !== d.origS || d.e !== d.origE || d.dayIdx !== d.origDay) {
        onItemMove(d.it, { fecha: dayIsos[d.dayIdx], hora_inicio: fmt(d.s), hora_fin: fmt(d.e) });
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, { once: true });
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [drag]); // eslint-disable-line react-hooks/exhaustive-deps

  const cols = `56px repeat(${days.length}, minmax(0, 1fr))`;

  return (
    <div className={`rounded-lg border overflow-hidden ${drag ? 'select-none' : ''}`} style={drag ? { cursor: drag.mode === 'move' ? 'grabbing' : 'ns-resize' } : undefined} data-testid="week-time-grid">
      <div className="grid border-b bg-muted/30" style={{ gridTemplateColumns: cols }}>
        <div className="text-[10px] text-muted-foreground p-1 self-end pb-2 text-right pr-2">todo el día</div>
        {days.map((d) => {
          const iso = localISO(d); const isToday = iso === todayISO;
          return (
            <div key={iso} className="border-l p-1.5 min-h-[64px] flex flex-col gap-1" data-testid={`week-allday-${iso}`}>
              <button type="button" onClick={() => onSlotClick?.(iso, null)} className="text-center group" title="Añadir evento de todo el día">
                <div className="text-[11px] text-muted-foreground uppercase">{WEEKDAYS[(d.getDay() + 6) % 7]}</div>
                <div className={`mx-auto w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold ${isToday ? 'bg-primary text-primary-foreground' : 'group-hover:bg-muted'}`}>{d.getDate()}</div>
              </button>
              {byDay[iso].allDay.map((it, idx) => {
                const cfg = cfgOf(it);
                return (
                  <button key={idx} type="button" onClick={() => onItemClick?.(it)} data-testid={`week-allday-item-${it.type}-${it.id}`}
                    className={`text-left text-[11px] px-1.5 py-0.5 rounded border-l-2 truncate ${cfg.block || `${cfg.dot} text-white`} ${it.completada ? 'line-through opacity-60' : ''}`}
                    title={`${cfg.label} · ${it.title}`}>
                    {it.privado && <Lock className="inline w-2.5 h-2.5 mr-0.5 -mt-0.5" />}{it.title}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 360px)', minHeight: 420 }} data-testid="week-time-scroll">
        <div ref={gridRef} className="grid relative" style={{ gridTemplateColumns: cols, height: 24 * HOUR_PX }}>
          <div className="relative">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="absolute right-2 text-[10px] text-muted-foreground -translate-y-1/2" style={{ top: h * HOUR_PX }}>{h > 0 ? `${String(h).padStart(2, '0')}:00` : ''}</div>
            ))}
          </div>
          {days.map((d, dayIdx) => {
            const iso = localISO(d); const isToday = iso === todayISO;
            // mientras se arrastra, el evento se pinta en su posición provisional
            let timed = byDay[iso].timed;
            if (drag) {
              timed = timed.filter((it) => it.id !== drag.id);
              if (drag.dayIdx === dayIdx) timed = [...timed, { ...drag.it, time: fmt(drag.s), end_time: fmt(drag.e), _dragging: true }];
            }
            const laid = layoutDay(timed);
            return (
              <div key={iso} className={`relative border-l ${isToday ? 'bg-primary/[0.03]' : ''}`} onClick={(e) => handleSlot(e, iso)} data-testid={`week-day-col-${iso}`}>
                {Array.from({ length: 24 }, (_, h) => (
                  <React.Fragment key={h}>
                    <div className="absolute left-0 right-0 border-t border-border/70" style={{ top: h * HOUR_PX }} />
                    <div className="absolute left-0 right-0 border-t border-dashed border-border/40" style={{ top: h * HOUR_PX + HOUR_PX / 2 }} />
                  </React.Fragment>
                ))}
                {laid.map(({ it, s, e, col, cols: n }, idx) => {
                  const cfg = cfgOf(it);
                  const w = 100 / n;
                  const draggable = canDrag(it) && !!onItemMove;
                  return (
                    <div key={`${it.id}-${idx}`} role="button" tabIndex={0}
                      onClick={(ev) => { ev.stopPropagation(); if (!draggable) onItemClick?.(it); }}
                      onPointerDown={(ev) => draggable && ev.button === 0 && startDrag(ev, it, s, e, dayIdx, 'move')}
                      className={`absolute rounded-md border-l-[3px] px-1.5 py-0.5 text-left overflow-hidden shadow-sm hover:shadow-md hover:z-20 transition-shadow ${cfg.block || `${cfg.dot} text-white`} ${it._dragging ? 'ring-2 ring-primary/60 shadow-lg opacity-90 z-40' : ''} ${draggable ? 'cursor-grab' : 'cursor-pointer'}`}
                      style={{ top: s * HOUR_PX / 60 + 1, height: Math.max((e - s) * HOUR_PX / 60 - 2, 18), left: `calc(${col * w}% + 2px)`, width: `calc(${w}% - 4px)`, zIndex: it._dragging ? 40 : 10 + col }}
                      title={`${cfg.label} · ${it.title} · ${it.time}${it.end_time ? '–' + it.end_time : ''}${draggable ? ' · arrastra para mover, borde inferior para alargar' : ''}`}
                      data-testid={`week-timed-item-${it.type}-${it.id}`}>
                      <div className="text-[11px] font-semibold leading-tight truncate">{it.privado && <Lock className="inline w-2.5 h-2.5 mr-0.5 -mt-0.5" />}{it.title}</div>
                      {(e - s) >= 40 && <div className="text-[10px] opacity-80 leading-tight truncate">{fmt(s)}–{fmt(e)}{it.subtitle ? ` · ${it.subtitle}` : ''}</div>}
                      {draggable && (
                        <div onPointerDown={(ev) => startDrag(ev, it, s, e, dayIdx, 'resize')} className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize" data-testid={`resize-handle-${it.id}`} />
                      )}
                    </div>
                  );
                })}
                {isToday && (
                  <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: nowMin * HOUR_PX / 60 }} data-testid="now-line">
                    <div className="h-[2px] bg-red-500" /><div className="w-2.5 h-2.5 rounded-full bg-red-500 -mt-[6px] -ml-1" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
